/**
 * Check Submissions UI (提出確認)
 *
 * Renders the overlay panel with:
 * - Close button
 * - (archive) hire-period tabs for previous / current cycles
 * - Roadmap (各種コピー / 記入書類)
 * - Content area: CheckSubmissionsVariousCopies or CheckSubmissionsFilledDocuments
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Submission } from '../types';
import CheckSubmissionsVariousCopies from './CheckSubmissionsVariousCopies';
import CheckSubmissionsFilledDocuments from './CheckSubmissionsFilledDocuments';
import ResubmissionEmailComposer from './resubmissionManagement/ResubmissionEmailComposer';
import type { BuiltResubmissionEmail, ResubmissionTemplatesOverride } from './resubmissionManagement/emailTemplates';
import { fetchMailTemplate } from '../../../../services/mailTemplateApi';
import { API_BASE_URL } from '../../../../config';
import {
  downloadOnboardingArchiveZip,
} from '../../../../services/onboardingFinalizeApi';
import {
  fetchEmployeeHirePeriods,
  type EmployeeHirePeriodRowApi,
} from '../../../../services/employeeHirePeriodApi';
import FinalizationModal from './FinalizationModal';
import ManageEmployeeModal from '../../empManagement/ManageEmployeeModal';
import EmployeePurgeModal from './EmployeePurgeModal';
import FinalizedArchiveViewer from './FinalizedArchiveViewer';
import { safeFileNamePart } from './utils';
import './CheckSubmissions.css';

/** review = active admin check; archive = read-only view for cleared/finalized employees */
export type CheckSubmissionsMode = 'review' | 'archive';

/** Filling documents count from chat-flow API */
export type RoadmapType = 'various' | 'filling';

type ArchivePeriodTab = {
  key: string;
  invitationId: string;
  label: string;
  finalizedAt: string;
  hasArchive: boolean;
  kind: 'archived' | 'in_progress';
};

export interface CheckSubmissionsProps {
  /** Submission row that was opened (employee context) */
  submission?: Submission | null;
  /** review (default) or archive read-only */
  mode?: CheckSubmissionsMode;
  /** Default / fallback archive invitation (last finalized). */
  archiveInvitationId?: string;
  archiveFinalizedAt?: string;
  /**
   * Current empaccount invitation (unused for archive tabs — live rounds stay in 書類管理).
   * Kept optional for callers; archive viewer only lists finalized hire periods.
   */
  currentInvitationId?: string;
  /** Called when user clicks the close (X) button */
  onClose: () => void;
  /**
   * Called after 書類審査結果メール sends successfully (approval or resubmission request).
   * Defaults to onClose so the admin returns to 書類管理 with refreshed list.
   */
  onMailSentSuccess?: () => void;
  /** After prepare-rehire: parent navigates to create-invitation */
  onStartRehire?: (participant: { email: string; name: string }) => void;
}

function buildArchiveTabs(
  periods: EmployeeHirePeriodRowApi[],
  _currentInvitationId: string | undefined,
  fallbackArchiveInvitationId: string | undefined,
  fallbackFinalizedAt: string | undefined
): ArchivePeriodTab[] {
  const tabs: ArchivePeriodTab[] = [];

  const sorted = [...periods].sort((a, b) => (a.periodNo || 0) - (b.periodNo || 0));
  for (const p of sorted) {
    const inv = p.invitationId?.trim();
    if (!inv) continue;
    // 従業員 archive viewer: only finalized periods (not the live rehire/submission round).
    if (!p.archivePath?.trim()) continue;

    const dateHint = p.hireDate || (p.finalizedAt ? p.finalizedAt.slice(0, 10) : '');
    tabs.push({
      key: `period-${p.periodNo}-${inv}`,
      invitationId: inv,
      label: dateHint ? `第${p.periodNo}期（${dateHint}）` : `第${p.periodNo}期`,
      finalizedAt: p.finalizedAt?.trim() || '',
      hasArchive: true,
      kind: 'archived',
    });
  }

  if (tabs.length === 0) {
    const fb = fallbackArchiveInvitationId?.trim() || '';
    if (fb && fallbackFinalizedAt?.trim()) {
      tabs.push({
        key: `fallback-${fb}`,
        invitationId: fb,
        label: 'アーカイブ',
        finalizedAt: fallbackFinalizedAt.trim(),
        hasArchive: true,
        kind: 'archived',
      });
    }
  }

  return tabs;
}

const CheckSubmissions: React.FC<CheckSubmissionsProps> = ({
  submission,
  mode = 'review',
  archiveInvitationId,
  archiveFinalizedAt,
  currentInvitationId,
  onClose,
  onMailSentSuccess,
  onStartRehire,
}) => {
  const archiveMode = mode === 'archive';
  const [showManageEmployee, setShowManageEmployee] = useState(false);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [activeRoadmap, setActiveRoadmap] = useState<RoadmapType>('various');
  const [variousState, setVariousState] = useState({ loading: true, total: 0 });
  const [fillingState, setFillingState] = useState({
    loading: true,
    completed: 0,
    total: 0,
    reviewed: 0,
  });

  const [showResubmissionEmail, setShowResubmissionEmail] = useState(false);
  const [builtResubmissionEmail, setBuiltResubmissionEmail] = useState<BuiltResubmissionEmail | null>(null);
  const [mailInvitationId, setMailInvitationId] = useState('');
  const [composerSession, setComposerSession] = useState(0);
  const [sendingResubmissionMail, setSendingResubmissionMail] = useState(false);
  const [reviewMailTemplates, setReviewMailTemplates] = useState<ResubmissionTemplatesOverride | null>(null);
  const [showFinalizationModal, setShowFinalizationModal] = useState(false);
  const [showManualFinalizeModal, setShowManualFinalizeModal] = useState(false);
  const [pendingFinalizeInvitationId, setPendingFinalizeInvitationId] = useState('');

  const [hirePeriods, setHirePeriods] = useState<EmployeeHirePeriodRowApi[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [selectedTabKey, setSelectedTabKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchMailTemplate('APPROVAL_NOTICE'), fetchMailTemplate('RESUBMISSION_REQUEST')])
      .then(([ap, re]) => {
        if (!cancelled) {
          setReviewMailTemplates({
            approved: { subject: ap.subject, body: ap.body },
            rejected: { subject: re.subject, body: re.body },
          });
        }
      })
      .catch(() => {
        if (!cancelled) setReviewMailTemplates(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!archiveMode) {
      setHirePeriods([]);
      setSelectedTabKey(null);
      return;
    }
    const email = submission?.email?.trim();
    if (!email) return;
    let cancelled = false;
    setPeriodsLoading(true);
    fetchEmployeeHirePeriods(email)
      .then((rows) => {
        if (!cancelled) setHirePeriods(rows);
      })
      .catch(() => {
        if (!cancelled) setHirePeriods([]);
      })
      .finally(() => {
        if (!cancelled) setPeriodsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [archiveMode, submission?.email]);

  const archiveTabs = useMemo(
    () =>
      archiveMode
        ? buildArchiveTabs(hirePeriods, currentInvitationId, archiveInvitationId, archiveFinalizedAt)
        : [],
    [archiveMode, hirePeriods, currentInvitationId, archiveInvitationId, archiveFinalizedAt]
  );

  useEffect(() => {
    if (!archiveMode || archiveTabs.length === 0) {
      setSelectedTabKey(null);
      return;
    }
    setSelectedTabKey((prev) => {
      if (prev && archiveTabs.some((t) => t.key === prev)) return prev;
      return archiveTabs[archiveTabs.length - 1].key;
    });
  }, [archiveMode, archiveTabs]);

  const selectedTab = archiveTabs.find((t) => t.key === selectedTabKey) ?? null;
  /** Archive viewer: only load a finalized period tab — never fall back to live empaccount invite. */
  const forcedInvitationId = archiveMode ? selectedTab?.invitationId?.trim() || null : null;

  const selectedHasArchive = selectedTab?.hasArchive === true || Boolean(selectedTab?.finalizedAt);
  const newestArchiveInvitationId =
    archiveTabs.length > 0 ? archiveTabs[archiveTabs.length - 1].invitationId : null;
  const hasActiveHire = hirePeriods.some((p) => (p.status || '').toLowerCase() === 'active');

  const isNewestArchiveTab =
    Boolean(forcedInvitationId) &&
    Boolean(newestArchiveInvitationId) &&
    forcedInvitationId === newestArchiveInvitationId;

  const archiveNeedsFinalize =
    archiveMode &&
    !periodsLoading &&
    archiveTabs.length === 0 &&
    Boolean(archiveInvitationId?.trim()) &&
    !archiveFinalizedAt?.trim();

  const handleArchiveCounts = useCallback((counts: { various: number; forms: number }) => {
    setVariousState({ loading: false, total: counts.various });
    setFillingState({
      loading: false,
      completed: counts.forms,
      total: counts.forms,
      reviewed: counts.forms,
    });
  }, []);

  const handleRequestResubmissionEmail = useCallback((built: BuiltResubmissionEmail, invitationId: string) => {
    setBuiltResubmissionEmail(built);
    setMailInvitationId(invitationId);
    setComposerSession((s) => s + 1);
    setShowResubmissionEmail(true);
  }, []);

  const handleProceedToFilling = useCallback(() => {
    setActiveRoadmap('filling');
  }, []);

  const handleSendResubmissionMail = async (payload: {
    subject: string;
    body: string;
    deadline: string | null;
  }) => {
    const to = submission?.email?.trim();
    if (!to) return;
    const built = builtResubmissionEmail;
    if (!built || !mailInvitationId.trim()) return;

    const notificationType =
      built.mode === 'approved' ? 'APPROVAL_NOTICE' : 'RESUBMISSION_REQUEST';

    setSendingResubmissionMail(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/mail/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          subject: payload.subject,
          body: payload.body,
          invitationId: mailInvitationId.trim(),
          notificationType,
          deadline: built.mode === 'rejected' && payload.deadline ? payload.deadline : null,
          signature: null,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string };
      if (res.ok && data.success) {
        setShowResubmissionEmail(false);
        setBuiltResubmissionEmail(null);
        if (notificationType === 'APPROVAL_NOTICE' && !archiveMode) {
          setPendingFinalizeInvitationId(mailInvitationId.trim());
          setShowFinalizationModal(true);
          setMailInvitationId('');
          return;
        }
        setMailInvitationId('');
        (onMailSentSuccess ?? onClose)();
      } else {
        alert(typeof data.message === 'string' ? data.message : '送信に失敗しました。');
      }
    } catch {
      alert('送信に失敗しました。');
    } finally {
      setSendingResubmissionMail(false);
    }
  };

  const variousLabel = variousState.loading ? '—' : `${variousState.total}/${variousState.total}`;
  const fillingLabel = fillingState.loading
    ? '—'
    : `${fillingState.completed}/${fillingState.total}`;

  const zipInvitationId = forcedInvitationId || (archiveNeedsFinalize ? archiveInvitationId : undefined);

  return (
    <div className="check-submissions">
      {archiveMode && (
        <div
          className={
            archiveNeedsFinalize
              ? 'check-submissions__archive-banner check-submissions__archive-banner--pending'
              : 'check-submissions__archive-banner'
          }
          role="status"
        >
          <span>
            {archiveNeedsFinalize
              ? 'アーカイブ未作成 — 登録完了処理を実行してください'
              : archiveTabs.length === 0 && !periodsLoading
                ? '表示できる登録完了アーカイブがありません'
                : '登録完了アーカイブ — 閲覧のみ'}
          </span>
          <div className="check-submissions__archive-banner-actions">
            {archiveNeedsFinalize && zipInvitationId && submission?.email && (
              <button
                type="button"
                className="check-submissions__archive-zip-btn check-submissions__archive-zip-btn--primary"
                onClick={() => setShowManualFinalizeModal(true)}
              >
                登録完了処理
              </button>
            )}
            {!archiveNeedsFinalize && selectedHasArchive && zipInvitationId && submission?.email && (
              <button
                type="button"
                className="check-submissions__archive-zip-btn"
                onClick={() => {
                  const name = safeFileNamePart(submission.empName?.trim() || submission.email) + '_書類一式.zip';
                  void downloadOnboardingArchiveZip(submission.email, zipInvitationId, name).catch(() => {
                    alert('ZIPのダウンロードに失敗しました。');
                  });
                }}
              >
                ZIPをダウンロード
              </button>
            )}
            <button
              type="button"
              className="check-submissions__archive-zip-btn"
              onClick={() => setShowManageEmployee(true)}
            >
              従業員を管理
            </button>
            {submission?.email && (
              <button
                type="button"
                className="check-submissions__archive-zip-btn check-submissions__archive-purge-btn"
                disabled={hasActiveHire || periodsLoading}
                title={
                  hasActiveHire
                    ? '在職中のため削除できません。先に退職を記録してください。'
                    : '従業員データを完全に削除'
                }
                onClick={() => setShowPurgeModal(true)}
              >
                データを完全削除
              </button>
            )}
          </div>
        </div>
      )}

      {archiveMode && (archiveTabs.length > 0 || periodsLoading) && (
        <div className="check-submissions__period-tabs" role="tablist" aria-label="雇用期間アーカイブ">
          {periodsLoading && archiveTabs.length === 0 && (
            <span className="check-submissions__period-tabs-loading">雇用履歴を読み込み中…</span>
          )}
          {archiveTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tab.key === selectedTabKey}
              className={
                tab.key === selectedTabKey
                  ? 'check-submissions__period-tab check-submissions__period-tab--active'
                  : 'check-submissions__period-tab'
              }
              onClick={() => {
                setSelectedTabKey(tab.key);
                setActiveRoadmap('various');
              }}
            >
              {tab.label}
              {tab.kind === 'in_progress' ? (
                <span className="check-submissions__period-tab-hint">進行中</span>
              ) : tab.hasArchive ? (
                <span className="check-submissions__period-tab-hint">完了</span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {showManageEmployee && submission?.email?.trim() && (
        <ManageEmployeeModal
          employeeName={submission.empName?.trim() || ''}
          employeeEmail={submission.email.trim()}
          onClose={() => setShowManageEmployee(false)}
          onStartRehire={(participant) => {
            setShowManageEmployee(false);
            onStartRehire?.(participant);
          }}
        />
      )}

      {showPurgeModal && submission?.email?.trim() && (
        <EmployeePurgeModal
          open={showPurgeModal}
          employeeName={submission.empName?.trim() || ''}
          employeeEmail={submission.email.trim()}
          onClose={() => setShowPurgeModal(false)}
          onPurged={() => {
            setShowPurgeModal(false);
            onClose();
          }}
        />
      )}

      {showManualFinalizeModal && submission?.email?.trim() && zipInvitationId?.trim() && (
        <FinalizationModal
          employeeName={submission.empName?.trim() || ''}
          employeeEmail={submission.email.trim()}
          invitationId={zipInvitationId.trim()}
          onClose={() => setShowManualFinalizeModal(false)}
          onComplete={() => {
            setShowManualFinalizeModal(false);
            onClose();
          }}
        />
      )}

      {showFinalizationModal && submission?.email?.trim() && pendingFinalizeInvitationId && (
        <FinalizationModal
          employeeName={submission.empName?.trim() || ''}
          employeeEmail={submission.email.trim()}
          invitationId={pendingFinalizeInvitationId}
          onClose={() => {
            setShowFinalizationModal(false);
            setPendingFinalizeInvitationId('');
            (onMailSentSuccess ?? onClose)();
          }}
          onComplete={() => {
            setShowFinalizationModal(false);
            setPendingFinalizeInvitationId('');
            (onMailSentSuccess ?? onClose)();
          }}
        />
      )}

      <button
        type="button"
        className="check-submissions__close"
        onClick={onClose}
        aria-label="閉じる"
      >
        ×
      </button>

      {showResubmissionEmail && builtResubmissionEmail && submission?.email?.trim() && (
        <div
          className="check-submissions__resubmission-email-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resubmission-email-title"
        >
          <ResubmissionEmailComposer
            key={composerSession}
            employeeName={submission.empName?.trim() || 'ご担当者'}
            employeeEmail={submission.email.trim()}
            mode={builtResubmissionEmail.mode}
            initialSubject={builtResubmissionEmail.subject}
            initialBody={builtResubmissionEmail.body}
            defaultSubject={builtResubmissionEmail.subject}
            defaultBody={builtResubmissionEmail.body}
            initialDeadlineIso={builtResubmissionEmail.deadlineIso}
            onClose={() => {
              setShowResubmissionEmail(false);
              setBuiltResubmissionEmail(null);
              setMailInvitationId('');
            }}
            onSend={handleSendResubmissionMail}
            sending={sendingResubmissionMail}
          />
        </div>
      )}

      <div className="check-submissions__body">
        <aside className="check-submissions__roadmap">
          <button
            type="button"
            className={`check-submissions__pill ${activeRoadmap === 'various' ? 'check-submissions__pill--active' : ''}`}
            onClick={() => setActiveRoadmap('various')}
          >
            アップロード書類 {variousLabel}
          </button>
          <div className="check-submissions__roadmap-line" aria-hidden />
          <button
            type="button"
            className={`check-submissions__pill ${activeRoadmap === 'filling' ? 'check-submissions__pill--active' : ''}`}
            onClick={() => setActiveRoadmap('filling')}
          >
            記入書類 {fillingLabel}
          </button>
        </aside>

        <div className="check-submissions__content">
          {archiveMode && !forcedInvitationId && !periodsLoading ? (
            <p className="check-submissions__archive-empty">
              {archiveNeedsFinalize
                ? 'アーカイブはまだ作成されていません。上の「登録完了処理」を実行してください。'
                : '登録完了済みのアーカイブのみ表示します。進行中の提出は「書類管理」で確認してください。'}
            </p>
          ) : archiveMode && forcedInvitationId && submission?.email?.trim() && !isNewestArchiveTab ? (
            <FinalizedArchiveViewer
              key={`archive-${forcedInvitationId}-${activeRoadmap}`}
              email={submission.email.trim()}
              invitationId={forcedInvitationId}
              employeeName={submission.empName?.trim() || undefined}
              section={activeRoadmap === 'various' ? 'various' : 'filling'}
              showProfileSections={false}
              onCountsChange={handleArchiveCounts}
            />
          ) : (
            <>
              {activeRoadmap === 'various' && (
                <CheckSubmissionsVariousCopies
                  key={`various-${forcedInvitationId || 'live'}`}
                  submission={submission}
                  archiveMode={archiveMode}
                  forcedInvitationId={forcedInvitationId}
                  onVariousStateChange={setVariousState}
                  onFillingStateChange={setFillingState}
                  onProceedToFilling={handleProceedToFilling}
                  onRequestResubmissionEmail={archiveMode ? undefined : handleRequestResubmissionEmail}
                  resubmissionTemplateOverride={reviewMailTemplates}
                />
              )}
              {activeRoadmap === 'filling' && (
                <CheckSubmissionsFilledDocuments
                  key={`filling-${forcedInvitationId || 'live'}`}
                  submission={submission}
                  archiveMode={archiveMode}
                  forcedInvitationId={forcedInvitationId}
                  onFillingStateChange={setFillingState}
                  onRequestResubmissionEmail={archiveMode ? undefined : handleRequestResubmissionEmail}
                  resubmissionTemplateOverride={reviewMailTemplates}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckSubmissions;
